import { Alert, Button, InputLabel, MenuItem, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import Modal, { CommonModalProps } from "..";
import { UploadCardFormatDto } from "../../../Core/Api/Types/db-format-infos";
import { PlatformDocument } from "../../../Core/Api/Types/platform-document";
import { PlatformDocumentType } from "../../../Core/Api/Types/platform-document-type";
import { GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../Redux/redux-store";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import UploadCard, { UploadCardRef, UploadContext } from "../../Cards/UploadCard/upload-card";
import FormInputSelect from "../../Forms/FormInputSelect/form-input-select";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { useTranslation } from "react-i18next";

export type ModalCreateOrUpdateDocumentElementProps = {
  format: UploadCardFormatDto;
  document?: PlatformDocument;
};

export type ModalCreateOrUpdateDocumentProps = CommonModalProps<ModalCreateOrUpdateDocumentElementProps>;

const ModalCreateOrUpdateDocument = React.forwardRef<any, ModalCreateOrUpdateDocumentProps>((props, ref) => {
  const { format, document, closeModal } = props;

  const uploadCardRef = React.useRef<UploadCardRef | null>(null);

  const mode = document ? "update" : "create";

  const [fileIsLoaded, setFileIsLoaded] = useState(false);
  const [type, setType] = useState<PlatformDocumentType>(document?.type || PlatformDocumentType.IngestSpecification);
  const [description, setDescription] = useState(document?.description || "");
  const [version, setVersion] = useState(document?.version || "");

  const enqueueSnackbar = useCustomSnackbar();
  const { t } = useTranslation(); // Initialize translation hook

  const formId = "modal-create-resource";
  const isFormValid = useAppSelector(GetFormIsValid(formId));

  const handleConfirmClick = () => {
    uploadCardRef.current?.performUpload();
  };

  return (
    <Modal
      title={mode === "create" ? t("modal:title.createDocument") : t("modal:title.updateDocument")}
      reference={ref}
      closeModal={closeModal}
      width={600}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button variant="outlined" onClick={closeModal}>
            {t("common:cancel")}
          </Button>
          <Button
            variant="outlined"
            color="success"
            onClick={handleConfirmClick}
            disabled={!isFormValid || !fileIsLoaded}
          >
            {mode === "create" ? t("button:addDocument") : t("button:updateDocument")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <FormInputSelect
          validationProps={{
            formId: formId,
            fieldId: "type",
            required: true,
            initialState: mode === "update",
            initialValue: type,
          }}
          stackProps={{}}
          label={t("common:type")}
          value={type}
          onChange={(event) => setType(event.target.value as PlatformDocumentType)}
        >
          {Object.keys(PlatformDocumentType).map((key) => (
            <MenuItem key={key} value={key}>
              {key}
            </MenuItem>
          ))}
        </FormInputSelect>
        <FormInputText
          validationProps={{
            formId: formId,
            fieldId: "description",
            required: true,
            initialState: mode === "update",
            initialValue: description,
          }}
          label={t("common:description")}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        <FormInputText
          validationProps={{
            formId: formId,
            fieldId: "version",
            required: true,
            initialState: mode === "update",
            initialValue: version,
          }}
          label={t("common:version")}
          value={version}
          onChange={(event) => setVersion(event.target.value)}
        />
        {mode === "update" && (
          <>
            <InputLabel>{t("field:label.currentFile")}</InputLabel>
            <Typography variant="body2">{document!.fileName || "N/A"}</Typography>
          </>
        )}
        <UploadCard
          ref={uploadCardRef}
          title={mode === "create" ? t("button:uploadDocument") : t("button:updateDocument")}
          context={mode === "create" ? UploadContext.CreatePlatformDocument : UploadContext.UpdatePlatformDocument}
          objectId={-500}
          format={format}
          userHasRightToDelete={false}
          platformDocProps={{
            description: description,
            fileType: type,
            version: version,
            documentId: document?.id,
          }}
          onSuccessUpload={() => {
            window.location.reload();
            closeModal();
          }}
          onDroppedFiles={(files) => {
            setFileIsLoaded(files.length > 0);
          }}
        />
        {mode === "update" && <Alert severity="info">{t("alert:info.documentReplaced")}</Alert>}
      </Stack>
    </Modal>
  );
});

export default ModalCreateOrUpdateDocument;
